/// <amd-module name="Test/TestSummary/Components/CompletedSummaryView" />
import * as React from 'react';
import { ITestAnalyticsLinkData } from 'Test/Analytics/Common/Types';
import * as Common from 'Test/common-util/Common';
import * as Telemetry from 'Test/common-util/Telemetry';
import * as TelemetryConstants from 'Test/common-util/TelemetryConstants';
import { DonutChartLegendComponent, IDonutChartLegendProps } from 'Test/TestSummary/Components/DonutChartLegend';
import { ResultSummaryNumberChartComponent, ValueType } from 'Test/TestSummary/Components/ResultSummaryDetailsChart';
import { getTestAnalyticsLinkData } from 'Test/TestSummary/Components/TestAnalyticsLink';
import { IPieChartData, IPieChartProps, ISummaryViewState } from 'Test/TestSummary/Report.types';
import * as Resources from 'Test/TestSummary/Resources';
import { SummaryViewStore } from 'Test/TestSummary/Store/SummaryViewStore';
import { ObservableValue } from 'VSS/Core/Observable';
import { format, localeFormat } from 'VSS/Core/Util/String';
import { DataSeries, Datum } from 'VSS/Features/Charts/Contracts';
import { FPSLinkComponent } from 'VSS/Platform/Components/FPSLink';
import { isFeatureFlagEnabled } from 'VSS/Platform/Feature';
import { IVssComponentContext, VssComponent, WrappedComponent } from 'VSS/Platform/Layout';
import { Card } from "VSSUI/Card";
import { UncheckedObserver} from "VSSUI/Observer";
import { css, getSafeId } from "VSSUI/Util";

export interface ICompletedSummaryProps {
    store: SummaryViewStore;
    artifactData: Common.IViewContextData;
    telemetryPropertiesForInProductLink: { [key: string]: any };
    isBuildTimeline?: boolean;
}

export class CompletedSummaryView extends VssComponent<ICompletedSummaryProps, ISummaryViewState, IVssComponentContext> {
    private _currentState: ISummaryViewState;
    private _chartDiameter: number;
    private _refSummary: any;
    private _paneSize = new ObservableValue<number | undefined>(undefined);

    constructor(props: ICompletedSummaryProps) {
        super(props);
        this.state = {} as ISummaryViewState;
        this._chartDiameter = 100;
    }

    public componentDidMount() {
        getTestAnalyticsLinkData(this.context, this.props.artifactData.viewContext, this.props.store.getDefinitionId()).then((testAnalyticsLinkData: ITestAnalyticsLinkData) => {
            this._currentState.testAnalyticsLinkData = testAnalyticsLinkData;
            this.setState(this._currentState);
        });
        this._logTelemetryForClick();
        this._getCurrentWidthOfSummary();
    }

    public componentDidUpdate() {
        this._getCurrentWidthOfSummary();
    }

    public render(): JSX.Element {
        if (this.props.store) {
            this._currentState = this.props.store.getState();
        }
        return this._getCompletedSummaryViewBody();
    }

    private _logTelemetryForClick() {
        let telemetryFeatureName: string = this.props.isBuildTimeline ? TelemetryConstants.featureTestTab_summaryInBuildTimelineRendered : TelemetryConstants.featureTestTab_TestTabClicked;
        if (this._currentState.totalTestsChartProps) {
            Telemetry.publishEvent(this.context.pageContext, this.props.artifactData, telemetryFeatureName, {
                [TelemetryConstants.inProgress]: false,
                [TelemetryConstants.totalTestsExists]: (this._currentState.totalTestsChartProps.aggregatedtotalTests > 0 ? "Yes" : "No"),
                [TelemetryConstants.failedTestsExists]: (this._currentState.totalTestsChartProps.totalFailures > 0 ? "Yes" : "No"),
                [TelemetryConstants.totalTests]: this._currentState.totalTestsChartProps.aggregatedtotalTests,
                [TelemetryConstants.failedTests]: this._currentState.totalTestsChartProps.totalFailures,
                [TelemetryConstants.passedTests]: this._currentState.totalTestsChartProps.totalPassed
            });
        }
    }

    private _getSummaryText(): string {
        if (this._currentState.aggregatedRunsOutcomeCount !== null && this._currentState.aggregatedRunsCount !== null) {
            if (this._currentState.aggregatedRunsOutcomeCount.notImpacted !== 0 && this._currentState.aggregatedRunsOutcomeCount.others !== 0) {
                return localeFormat(Resources.RunsSummaryStatusWithImpactedAndOtherRuns,
                    this._currentState.aggregatedRunsCount.completedRuns,
                    this._currentState.aggregatedRunsOutcomeCount.passed,
                    this._currentState.aggregatedRunsOutcomeCount.failed,
                    this._currentState.aggregatedRunsOutcomeCount.notImpacted,
                    this._currentState.aggregatedRunsOutcomeCount.others);
            } else if (this._currentState.aggregatedRunsOutcomeCount.notImpacted !== 0 && this._currentState.aggregatedRunsOutcomeCount.others === 0) {
                return localeFormat(Resources.RunsSummaryStatusWithImpactedRunsOnly,
                    this._currentState.aggregatedRunsCount.completedRuns,
                    this._currentState.aggregatedRunsOutcomeCount.passed,
                    this._currentState.aggregatedRunsOutcomeCount.failed,
                    this._currentState.aggregatedRunsOutcomeCount.notImpacted);
            } else if (this._currentState.aggregatedRunsOutcomeCount.notImpacted === 0 && this._currentState.aggregatedRunsOutcomeCount.others !== 0) {
                return localeFormat(Resources.RunsSummaryStatusWithImpactedAndOtherRuns,
                    this._currentState.aggregatedRunsCount.completedRuns,
                    this._currentState.aggregatedRunsOutcomeCount.passed,
                    this._currentState.aggregatedRunsOutcomeCount.failed,
                    this._currentState.aggregatedRunsOutcomeCount.others);
            } else {
                return localeFormat(Resources.RunsSummaryStatus,
                    this._currentState.aggregatedRunsCount.completedRuns,
                    this._currentState.aggregatedRunsOutcomeCount.passed,
                    this._currentState.aggregatedRunsOutcomeCount.failed);
            }
        } else {
            return "";
        }
    }

    private _getCompletedSummaryViewBody(): JSX.Element {
        const isReportCustomizationFeatureEnabled: boolean = isFeatureFlagEnabled(this.context.pageContext, Common.FeatureFlagConstants.TriReportCustomization, false);
        const isAnyDifferenceValuePresentInNumberCharts: boolean = this._isAnyDifferenceValuePresentInNumberCharts();
        const requiredSize = this._getRequiredDisplaySize();
        return (
            <div ref={(SummaryContainer: HTMLDivElement) => (this._refSummary = SummaryContainer)}>
                <UncheckedObserver paneSize={this._paneSize}>
                    {/* TODO: the argument to this function isn't right at all */}
                    {(paneSize: number | undefined) => {
                        if (paneSize === undefined) {
                            return null;
                        }
                        else {
                        const valueToRead = this._paneSize.value as number;
                        const stats = this._getCompletedSummaryViewItems(valueToRead, requiredSize);
                        return (
                        <div className="summary-view-parent">
                            {
                                this._currentState &&
                                this._currentState.aggregatedRunsCount &&
                                (this._currentState.aggregatedRunsCount.completedRuns > 0 || this._currentState.aggregatedRunsCount.abortedRuns > 0) &&
                                <div className="summary-view-container">
                                    {
                                        <div className="summary-view-runs-info-bar">
                                            {
                                                this._currentState.aggregatedRunsCount &&
                                                this._currentState.aggregatedRunsCount.completedRuns > 0 &&
                                                this._currentState.aggregatedRunsOutcomeCount &&
                                                <div className="summary-view-completed-runs">
                                                    {
                                                        this._getSummaryText()
                                                    }
                                                    {this.state.testAnalyticsLinkData && this.state.testAnalyticsLinkData.testAnalyticsLinkMessage && !this.props.isBuildTimeline &&
                                                        <FPSLinkComponent className="failing-test-analytics-link"
                                                            href={this.state.testAnalyticsLinkData.testAnalyticsLink}
                                                            telemetrySource={TelemetryConstants.featureAnalyticsInProductLink_UniqueFailingTests_TestTab}
                                                            telemetryProperties={this.props.telemetryPropertiesForInProductLink} >
                                                            {this.state.testAnalyticsLinkData.testAnalyticsLinkMessage}
                                                        </FPSLinkComponent>
                                                    }
                                                    {isFeatureFlagEnabled(this.context.pageContext, Common.FeatureFlagConstants.ShowCloudTestLink) &&
                                                        <WrappedComponent
                                                            dependencies={["ms.vss-test-web.test-cloud-test-link"]}
                                                            wrappedType={"cloud-test-link"}
                                                            wrappedProps={{
                                                                artifactData: this.props.artifactData
                                                            }}
                                                        />
                                                    }
                                                </div>
                                            }
                                            {
                                                this._currentState.aggregatedRunsCount.abortedRuns > 0 &&
                                                !this._currentState.onlyNotReportedTests &&
                                                <div className="aborted-view-bar">
                                                    {this._currentState &&
                                                        this._currentState.aggregatedRunsCount &&
                                                        this._currentState.aggregatedRunsCount.completedRuns > 0 &&
                                                        <div className="aborted-runs-separator-in-bar" />
                                                    }
                                                    <div className="aborted-runs">
                                                        <span className="aborted-runs-value"> {this._currentState.aggregatedRunsCount.abortedRuns.toLocaleString()} </span>
                                                        {Resources.RunsAborted}
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    }
                                    <Card className="test-summary-card">
                                        <div className={valueToRead >= requiredSize ? "test-summary-card-row-big" : "test-summary-card-row-small"} >
                                            {stats.map((items, index) => (
                                                <div className="flex-column" style={{ minWidth: "50px" }} key={index}>
                                                    {items}
                                                </div>
                                            ))}
                                        </div>
                                    </Card>
                                </div>
                            }
                        </div>
                    );
                }}}
                </UncheckedObserver>
            </div>
        );
    }

    private _getCompletedSummaryViewItems(paneSize: number, requiredSize: number): JSX.Element[] {
        const isAnyDifferenceValuePresentInNumberCharts: boolean = this._isAnyDifferenceValuePresentInNumberCharts();
        const isReportCustomizationFeatureEnabled: boolean = isFeatureFlagEnabled(this.context.pageContext, Common.FeatureFlagConstants.TriReportCustomization, false);
        // Validate that enough real estate exists on screen to draw full view else draw small view
        const isScreenSizeSmall = paneSize < requiredSize;
        let summaryItems = [];
        // pushing completed runs summary items
        if (this._currentState.aggregatedRunsCount && this._currentState.aggregatedRunsCount.completedRuns > 0) {

            if (this._currentState && this._currentState.totalTestsNumberChartProps && !this._currentState.onlyNotReportedTests) {
                if(!isScreenSizeSmall){
                summaryItems.push(<ResultSummaryNumberChartComponent
                    {...this._currentState.totalTestsNumberChartProps}
                    isVerticalAlignWithDifferencePresent={isAnyDifferenceValuePresentInNumberCharts}
                />)
                }
                else if (this._currentState.totalTestsChartProps){                                      
                        summaryItems.push(this._smallFailedAndPassedData(this._currentState.totalTestsChartProps.aggregatedtotalTests, Resources.TotalTestsText, [this._currentState.totalTestsChartProps.totalPassed, this._currentState.totalTestsChartProps.totalFailures, this._currentState.totalTestsChartProps.otherTests]))                   
                }
            }

            if (this._currentState && !this._currentState.onlyNotReportedTests && !isScreenSizeSmall) {
                summaryItems.push(<div className="separator" />)
            }


            if (this._currentState && this._currentState.totalTestsChartProps && !this._currentState.onlyNotReportedTests && !isScreenSizeSmall) {
                    summaryItems.push(this._getTotalTestsChart())
            }

            if (this._currentState && this._currentState.totalTestsChartProps && this._currentState.testFailuresChartProps && (this._currentState.testFailuresChartProps.totalFailures > 0) && !this._currentState.onlyNotReportedTests) {
                if(!isScreenSizeSmall){
                    summaryItems.push(this._getFailedTestsChart());
                }
                else{
                    summaryItems.push(this._smallFailedAndPassedData(this._currentState.testFailuresChartProps.totalFailures, Resources.FailedTestsHeading, [this._currentState.testFailuresChartProps.newFailures, this._currentState.testFailuresChartProps.existingFailures]))
                }
            }

            if (this._currentState && this._currentState.passPercentageNumberChartProps && !this._currentState.onlyNotReportedTests) {
                summaryItems.push(<ResultSummaryNumberChartComponent
                    {...this._currentState.passPercentageNumberChartProps}
                    isVerticalAlignWithDifferencePresent={isAnyDifferenceValuePresentInNumberCharts}
                    isScreenSizeSmall={isScreenSizeSmall}
                />)
            }


            if (this._currentState && this._currentState.runDurationNumberChartProps && !this._currentState.onlyNotReportedTests) {
                summaryItems.push(<ResultSummaryNumberChartComponent
                    {...this._currentState.runDurationNumberChartProps}
                    isVerticalAlignWithDifferencePresent={isAnyDifferenceValuePresentInNumberCharts}
                    isScreenSizeSmall={isScreenSizeSmall}
                />)
            }


            if (isReportCustomizationFeatureEnabled && this._currentState && this._currentState.notReportedNumberChartProps) {
                summaryItems.push(<ResultSummaryNumberChartComponent
                    {...this._currentState.notReportedNumberChartProps}
                    isVerticalAlignWithDifferencePresent={isAnyDifferenceValuePresentInNumberCharts}
                    isScreenSizeSmall={isScreenSizeSmall}
                />)
            }
        }
        if (this._currentState &&
            this._currentState.aggregatedRunsCount &&
            this._currentState.aggregatedRunsCount.abortedRuns > 0 &&
            !this._currentState.onlyNotReportedTests) {

            if (this._currentState &&
                this._currentState.aggregatedRunsCount &&
                this._currentState.aggregatedRunsCount.completedRuns > 0 && !isScreenSizeSmall) {
                summaryItems.push(<div className="aborted-runs-separator" />);
            }

            if (this._currentState && this._currentState.totalAbortedTestsNumberChartProps && !isScreenSizeSmall) {
                summaryItems.push(<ResultSummaryNumberChartComponent
                    {...this._currentState.totalAbortedTestsNumberChartProps}
                    isVerticalAlignWithDifferencePresent={isAnyDifferenceValuePresentInNumberCharts}
                />)
            }
            if (this._currentState &&
                this._currentState.abortedTestsChartProps &&
                this._currentState.abortedTestsChartProps.totalTests > 0) {
                    // If total test chart props are present then its not a aborted run only scenario hence even in small screen we can show complete aborted view
                if(!isScreenSizeSmall || ((this._currentState.totalTestsChartProps && this._currentState.totalTestsChartProps.aggregatedtotalTests == 0) && paneSize > 650)){
                    summaryItems.push(this._getAbortedTestsChart());
                }
                else{
                    summaryItems.push(this._smallFailedAndPassedData(this._currentState.abortedTestsChartProps.totalTests, Resources.TotalAbortedRunText, [this._currentState.abortedTestsChartProps.totalPassed, this._currentState.abortedTestsChartProps.totalFailures,
                        this._currentState.abortedTestsChartProps.otherTests, this._currentState.abortedTestsChartProps.totalAborted,
                        this._currentState.abortedTestsChartProps.totalNotImpactedTests]))
                }
            }

        }
        return summaryItems;
    }

    private _getRequiredDisplaySize(): number{
        /* This method validates what elements need to be drawn and then returns the required space to draw them*/
        let mode = 0;
        if (!this._currentState.onlyNotReportedTests){
            mode++;
        }
        if (this._currentState.abortedTestsChartProps && this._currentState.abortedTestsChartProps.totalTests > 0){
            mode++;
        }
        if (this._currentState.notReportedNumberChartProps && this._currentState.notReportedNumberChartProps.value > 0){
            mode++;
        }
        if (mode == 3){
            return 1500;
        }
        else if (mode == 2){
            return 1350;
        }
        return 1024;
    }

    private _smallFailedAndPassedData(data: number, title: string, SecondRowData: number[]): JSX.Element {
        let itemsToShow: string = "";
        if (title===Resources.TotalTestsText){
            itemsToShow = localeFormat(Resources.ResultsSmallViewStatus, SecondRowData[0], SecondRowData[1], SecondRowData[2])
        }
        if (title===Resources.TotalAbortedRunText){
            itemsToShow = localeFormat(Resources.AbortedResultsSmallView, SecondRowData[0], SecondRowData[1], SecondRowData[2], SecondRowData[3], SecondRowData[4]);
        }
        if (title===Resources.FailedTestsHeading){
            itemsToShow = localeFormat(Resources.FailedResultsSmallView, SecondRowData[0], SecondRowData[1]);
        }
        return (
            <div className={css("flex-grow","flex-row flex-self-start","formatted-summary-string")}>
                    <div className={css("secondary-text summary-line-compact")} id={getSafeId(title)}>
                        {title}:
                    </div> 
                    <div className={css("summary-line-smallWindow flex-center flex-column")}>
                    {data} {itemsToShow}
                    </div>
            </div>
        )
    }

    private _getCurrentWidthOfSummary() {
        if(!!this._refSummary){
            this._paneSize.value = this._refSummary.clientWidth ;
        }
    }

    private _isAnyDifferenceValuePresentInNumberCharts(): boolean {
        // If any of the number charts (run duration, total tests, pass percentage etc.) contains a difference value, we have to align all other independent components accordingly
        // To achieve this, we are first checking if any of the component has difference value present, then we pass this information to all other components in props to align accordingly
        let result: boolean = false;
        const isDifferenceValuePresentInAnyNumberChart: boolean | null =
            (this._currentState.totalTestsNumberChartProps && this._currentState.totalTestsNumberChartProps.difference && this._currentState.totalTestsNumberChartProps.difference.valueType !== ValueType.Unchanged)
            || (this._currentState.passPercentageNumberChartProps && this._currentState.passPercentageNumberChartProps.difference && this._currentState.passPercentageNumberChartProps.difference.valueType !== ValueType.Unchanged)
            || (this._currentState.runDurationNumberChartProps && this._currentState.runDurationNumberChartProps.difference && this._currentState.runDurationNumberChartProps.difference.valueType !== ValueType.Unchanged)
            || (this._currentState.totalAbortedTestsNumberChartProps && this._currentState.totalAbortedTestsNumberChartProps.difference && this._currentState.totalAbortedTestsNumberChartProps.difference.valueType !== ValueType.Unchanged)
            || (this._currentState.notReportedNumberChartProps && this._currentState.notReportedNumberChartProps.difference && this._currentState.notReportedNumberChartProps.difference.valueType !== ValueType.Unchanged);
        if (isDifferenceValuePresentInAnyNumberChart) {
            result = true;
        }
        return result;
    }

    private _getTotalTestsChart(): JSX.Element {
        const props: IPieChartProps = this._getTotalTestsChartProps();
        let totalTestsChartData: Datum[] = [];
        totalTestsChartData.push({
            y: this._currentState.totalTestsChartProps!.totalPassed,
            name: Resources.PassedText,
            color: Common.TestReportColorPalette.Passed
        });
        totalTestsChartData.push({
            y: this._currentState.totalTestsChartProps!.totalFailures,
            name: Resources.FailedText,
            color: Common.TestReportColorPalette.Failed
        });
        totalTestsChartData.push({
            y: this._currentState.totalTestsChartProps!.otherTests,
            name: Resources.OthersText,
            color: Common.TestReportColorPalette.OtherOutcome
        });
        totalTestsChartData.push({
            y: this._currentState.totalTestsChartProps!.totalNotImpactedTests,
            name: Resources.NotImpactedLegendText,
            color: Common.TestReportColorPalette.NotImpacted
        });
        let hostOptions = {
            width: this._chartDiameter,
            height: this._chartDiameter
        };
        let specialOptions = {
            size: "120%",
            innerSize: "60%",
            showLabels: false
        };
        let series = [{ data: totalTestsChartData ? totalTestsChartData : [] }];
        const donutChartLegendProps: IDonutChartLegendProps = this._getDonutChartLegendProps(props);
        return <div className="summary-view-totaltests-chart">
            <WrappedComponent
                dependencies={["ms.vss-features.vss-charts-common"]}
                wrappedType="vss-chart"
                pageContext={this.context.pageContext}
                hostOptions={hostOptions}
                specializedOptions={specialOptions}
                title={Resources.TotalTestsHeading}
                chartType={"pie"}
                series={series}
                suppressMargin={true}
                suppressAnimation={true}
                legend={{ enabled: false }}
                xAxis={{
                    labelFormatMode: "textual",
                    labelValues: CompletedSummaryView._getXLables(totalTestsChartData)
                }}
            />
            <DonutChartLegendComponent {...donutChartLegendProps} />
        </div>;
    }

    private static _getXLables(chartProps: Datum[]) {
        return chartProps ? chartProps.map(chartData => chartData.name || "") : [];
    }

    private _getFailedTestsChart(): JSX.Element {
        const props: IPieChartProps = this._getFailedTestsChartProps();
        const donutChartLegendProps: IDonutChartLegendProps = this._getDonutChartLegendProps(props);
        let totalTestsChartData: Datum[] = [];
        totalTestsChartData.push({
            y: this._currentState.testFailuresChartProps!.newFailures,
            name: Resources.NewFailuresLegendText,
            color: Common.TestReportColorPalette.Failed
        });
        totalTestsChartData.push({
            y: this._currentState.testFailuresChartProps!.existingFailures,
            name: Resources.ExistingFailuresLegendText,
            color: Common.TestReportColorPalette.ExistingFailures
        });
        let hostOptions = {
            width: this._chartDiameter,
            height: this._chartDiameter
        };
        let specialOptions = {
            size: "120%",
            innerSize: "60%",
            showLabels: false
        };
        let series: DataSeries[] = [{ name: "FailedTests", data: totalTestsChartData ? totalTestsChartData : [] }];
        return <div className="summary-view-failedtests-chart">
            <WrappedComponent
                dependencies={["ms.vss-features.vss-charts-common"]}
                wrappedType="vss-chart"
                pageContext={this.context.pageContext}
                hostOptions={hostOptions}
                specializedOptions={specialOptions}
                title={Resources.FailedTestsHeading}
                chartType={"pie"}
                series={series}
                suppressMargin={true}
                suppressAnimation={true}
                legend={{ enabled: false }}
                xAxis={{
                    labelFormatMode: "textual",
                    labelValues: CompletedSummaryView._getXLables(totalTestsChartData)
                }}
            />
            <div className="failure-chart-legend-area">
                {this._getFailureChartLabelDiv(props)}
                <DonutChartLegendComponent {...donutChartLegendProps} />
            </div>
        </div>;
    }

    private _getAbortedTestsChart(): JSX.Element {
        const props: IPieChartProps = this._getAbortedTestsChartProps();
        const donutChartLegendProps: IDonutChartLegendProps = this._getDonutChartLegendProps(props);
        return <div className="summary-view-abortedtests-chart">
            <DonutChartLegendComponent {...donutChartLegendProps} />
        </div>;
    }

    private _getDonutChartLegendProps(props: IPieChartProps): IDonutChartLegendProps {
        let donutChartLegendProps: IDonutChartLegendProps = {
            legend: props.data.map((d) => {
                return {
                    count: d.dataCount.toLocaleString(),
                    label: d.dataValue,
                    color: d.dataColor,
                    sublabel: d.dataSubText
                };
            })
        };
        return donutChartLegendProps;
    }

    private _getFailureChartLabelDiv(props: IPieChartProps): JSX.Element {
        let failuresIncrementString: string;
        let failuresIncrementClassName: string;
        let totalLabel: string;
        let diffLabel: string;
        if (!this._currentState.testFailuresChartProps) {
            return <div>"Failure Chart props are empty."</div>;
        }
        const increaseInFailures: number = this._currentState.testFailuresChartProps.increaseInFailures;

        if (increaseInFailures > 0) {
            failuresIncrementClassName = "failures-increased";
            failuresIncrementString = format("(+{0})", increaseInFailures.toLocaleString());
            diffLabel = "Failures increased by " + increaseInFailures.toLocaleString();
        } else if (increaseInFailures < 0) {
            failuresIncrementClassName = "failures-decreased";
            failuresIncrementString = format("({0})", increaseInFailures.toLocaleString());
            diffLabel = "Failures decreased by " + Math.abs(increaseInFailures).toLocaleString();
        } else {
            failuresIncrementClassName = "failures-unchanged";
            failuresIncrementString = "";
            diffLabel = "Failures unchanged"
        }
        totalLabel = props.totalCount.toString() + " " + props.title + " " + diffLabel;
        return <div className="failure-chart-label-div">
            <div role="img" aria-label={totalLabel} className="failed-chart-total-count">{props.totalCount}</div>
            <div aria-hidden="true" className="failed-chart-title">{props.title}</div>
            <div aria-hidden="true" className={`${failuresIncrementClassName} failed-chart-test-increment`}>{failuresIncrementString.toLocaleString()}</div>
        </div>;
    }

    private _getTotalTestsChartProps(): IPieChartProps {
        const totalTestsPieChartProps: IPieChartProps = {} as IPieChartProps;
        if (!this._currentState.totalTestsChartProps) {
            return totalTestsPieChartProps;
        }
        totalTestsPieChartProps.title = Resources.TotalTestsHeading;
        totalTestsPieChartProps.totalCount = this._currentState.totalTestsChartProps.aggregatedtotalTests;
        totalTestsPieChartProps.data = [] as IPieChartData[];
        totalTestsPieChartProps.data.push({
            dataValue: Resources.PassedText,
            dataCount: this._currentState.totalTestsChartProps.totalPassed,
            dataColor: Common.TestReportColorPalette.Passed,
            dataSubText: this._currentState.totalTestsChartProps.totalPassedOnRerun > 0 ? format(Resources.PassedOnRerunLegendText, this._currentState.totalTestsChartProps.totalPassedOnRerun) : null
        } as IPieChartData);
        totalTestsPieChartProps.data.push({
            dataValue: Resources.FailedText,
            dataCount: this._currentState.totalTestsChartProps.totalFailures,
            dataColor: Common.TestReportColorPalette.Failed
        });
        totalTestsPieChartProps.data.push({
            dataValue: Resources.OthersText,
            dataCount: this._currentState.totalTestsChartProps.otherTests,
            dataColor: Common.TestReportColorPalette.OtherOutcome
        });

        if (this._currentState.totalTestsChartProps.totalNotImpactedTests > 0) {
            totalTestsPieChartProps.data.push({
                dataValue: Resources.NotImpactedLegendText,
                dataCount: this._currentState.totalTestsChartProps.totalNotImpactedTests,
                dataColor: Common.TestReportColorPalette.NotImpacted
            });
        }

        return totalTestsPieChartProps;
    }

    private _getFailedTestsChartProps(): IPieChartProps {
        const failedTestsPieChartProps: IPieChartProps = {} as IPieChartProps;
        if (!this._currentState.testFailuresChartProps) {
            return failedTestsPieChartProps;
        }
        failedTestsPieChartProps.title = Resources.FailedTestsHeading;
        failedTestsPieChartProps.totalCount = this._currentState.testFailuresChartProps.totalFailures;
        failedTestsPieChartProps.data = [] as IPieChartData[];
        failedTestsPieChartProps.data.push({
            dataValue: Resources.NewFailuresLegendText,
            dataCount: this._currentState.testFailuresChartProps.newFailures,
            dataColor: Common.TestReportColorPalette.Failed
        });
        failedTestsPieChartProps.data.push({
            dataValue: Resources.ExistingFailuresLegendText,
            dataCount: this._currentState.testFailuresChartProps.existingFailures,
            dataColor: Common.TestReportColorPalette.ExistingFailures
        });

        return failedTestsPieChartProps;
    }

    private _getAbortedTestsChartProps(): IPieChartProps {
        const abortedTestsPieChartProps: IPieChartProps = {} as IPieChartProps;
        abortedTestsPieChartProps.data = [] as IPieChartData[];

        if (!this._currentState.abortedTestsChartProps) {
            return abortedTestsPieChartProps;
        }

        abortedTestsPieChartProps.data.push({
            dataValue: Resources.PassedText,
            dataCount: this._currentState.abortedTestsChartProps.totalPassed,
            dataColor: Common.TestReportColorPalette.Passed,
        });
        abortedTestsPieChartProps.data.push({
            dataValue: Resources.FailedText,
            dataCount: this._currentState.abortedTestsChartProps.totalFailures,
            dataColor: Common.TestReportColorPalette.Failed
        });
        abortedTestsPieChartProps.data.push({
            dataValue: Resources.TestOutcome_Aborted,
            dataCount: this._currentState.abortedTestsChartProps.totalAborted,
            dataColor: Common.TestReportColorPalette.AbortedForLegend
        });
        abortedTestsPieChartProps.data.push({
            dataValue: Resources.OthersText,
            dataCount: this._currentState.abortedTestsChartProps.otherTests,
            dataColor: Common.TestReportColorPalette.OtherOutcome
        });
        if (this._currentState.abortedTestsChartProps.totalNotImpactedTests > 0) {
            abortedTestsPieChartProps.data.push({
                dataValue: Resources.NotImpactedLegendText,
                dataCount: this._currentState.abortedTestsChartProps.totalNotImpactedTests,
                dataColor: Common.TestReportColorPalette.NotImpacted
            });
        }
        return abortedTestsPieChartProps;
    }
}